import * as React from "react"
import { css, cx } from "@emotion/css"
import { useI18n } from "providers/I18n"

import ButtonBase from "@material-ui/core/ButtonBase"
import { ReactComponent as UploadIcon } from "assets/upload.svg"

import { DropzoneState } from "react-dropzone"
import { allAcceptableFilesPhrase } from "./utils"

const styles = {
  hidden: css`
    display: none;
  `,
  zone: css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2.5rem 1.5rem;
    border-radius: 6px;
    width: inherit;
    max-height: 260px;
    min-height: 288px;
    width: 100%;

    border: dashed 1.4px var(--light-grey);

    background-color: rgba(231, 230, 230, 0.1);

    :focus {
      outline: none;
      border-color: var(--primary-main);
    }
  `,
  container: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    min-height: 105px;
    text-align: center;
  `,
  rejected: css`
    background-color: var(--lighter-red);
    border: 2px dashed var(--red);
  `,
  accepted: css`
    background-color: rgba(0, 177, 163, 0.01);
    border: 2px dashed var(--primary-main);
  `,

  icon: css`
    color: var(--primary-main);
    width: var(--spacing-xxl-5);
    height: var(--spacing-xxl-5);
    padding-top: var(--spacing-m);
    margin-bottom: var(--spacing-xs);
  `,

  browseBtn: css`
    text-transform: lowercase;
  `,

  acceptableFilesLabel: css`
    padding: 0.5rem;
    margin: 0;
    text-transform: lowercase;
    text-align: center;
    padding-top: var(--spacing-m);
  `,
}

type DropAreaProps = {
  documentsLabel?: string
  acceptableFilesLabel?: string
  className?: string
  //
  dropzoneState: DropzoneState
}

export function DropArea(props: DropAreaProps) {
  const {
    acceptableFilesLabel = allAcceptableFilesPhrase,
    documentsLabel,
    className,
    //
    dropzoneState: ds,
  } = props
  const { t } = useI18n()

  const handleOpen = (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    evt.stopPropagation()
    ds.open()
  }

  return (
    <div
      className={cx("DropArea", styles.zone, className, {
        [styles.accepted]: ds.isDragAccept,
        [styles.rejected]: ds.isDragReject,
      })}
      {...ds.getRootProps()}
    >
      <div className={cx("dropzone-container", styles.container)}>
        <input data-testid="drop-area-input" {...ds.getInputProps()} />
        <div className={styles.icon}>
          <UploadIcon />
        </div>

        <span className={cx({ [styles.hidden]: !ds.isDragAccept })}>
          {t("droparea.isDragAccept")}
        </span>

        <div className={cx({ [styles.hidden]: ds.isDragAccept })}>
          <p className="BodyBody-1BlackRegular">{t("droparea.title")}</p>
          <span className="BodyBody-1Dark-greyRegular">
            {documentsLabel || t("droparea.documents")}{" "}
          </span>
          <span className="BodyBody-1Dark-greyRegular">
            {t("droparea.here.or")}{" "}
          </span>
          <ButtonBase
            data-testid="button-base"
            className={cx("BodyBody-1Primary-mainRegular", styles.browseBtn)}
            onClick={handleOpen}
          >
            {t("droparea.browse")}
          </ButtonBase>
        </div>

        <p
          className={cx("BodyBody-xsBlackLight", styles.acceptableFilesLabel, {
            [styles.hidden]: ds.isDragAccept,
          })}
        >
          {acceptableFilesLabel}
        </p>
        {ds.isDragReject && (
          <span className={"BodyBody-1RedRegular"}>
            {t("droparea.unsupported.file")}
          </span>
        )}
      </div>
    </div>
  )
}
